import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import Header from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Settings,
  Plus,
  Pencil,
  Trash2,
  TrendingUp,
  TrendingDown,
  Filter,
  Building2,
  Shield,
  Megaphone,
  DollarSign,
  Users,
  Eye,
  Calendar
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KPIDefinitions = () => {
  const { isAdmin } = useAuth();
  const { selectedYear } = useApp();
  const [definitions, setDefinitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [functionFilter, setFunctionFilter] = useState('all');
  
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isMonthlyPlansDialogOpen, setIsMonthlyPlansDialogOpen] = useState(false);
  const [selectedDef, setSelectedDef] = useState(null);
  const [monthlyPlans, setMonthlyPlans] = useState({});
  const [savingPlans, setSavingPlans] = useState(false);
  
  const [formData, setFormData] = useState({
    metric_name: '',
    function: 'brokerage',
    section: 'General',
    unit: '%',
    directionality: 'higher_is_better',
    plan_value: '',
    green_threshold: 0,
    yellow_threshold: -0.05,
    description: '',
    // Plan-aware fields
    plan_cadence: 'monthly',
    weekly_tracking_enabled: false,
    weekly_pacing_method: 'linear',
    monthly_plan_required: true,
    quarterly_plan_required: false,
    annual_plan_required: false,
    // Vertical/Segment tracking
    segments_enabled: false
  });

  const departments = [
    { value: 'brokerage', label: 'Brokerage', icon: Building2 },
    { value: 'mga', label: 'MGA', icon: Shield },
    { value: 'marketing', label: 'Marketing', icon: Megaphone },
    { value: 'finance', label: 'Finance', icon: DollarSign },
    { value: 'people', label: 'People', icon: Users },
  ];

  const units = ['%', '$', '$M', '$K', '#', 'days', 'months', 'score', 'K', 'hrs'];
  
  const planCadences = [
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'annual', label: 'Annual' }
  ];

  const months = [
    { key: '01', label: 'Jan' }, { key: '02', label: 'Feb' }, { key: '03', label: 'Mar' },
    { key: '04', label: 'Apr' }, { key: '05', label: 'May' }, { key: '06', label: 'Jun' },
    { key: '07', label: 'Jul' }, { key: '08', label: 'Aug' }, { key: '09', label: 'Sep' },
    { key: '10', label: 'Oct' }, { key: '11', label: 'Nov' }, { key: '12', label: 'Dec' }
  ];

  const fetchDefinitions = useCallback(async () => {
    try {
      const params = {};
      if (functionFilter !== 'all') params.function = functionFilter;
      
      const response = await axios.get(`${API}/kpi-definitions`, { params });
      setDefinitions(response.data);
    } catch (error) {
      console.error('Failed to fetch definitions:', error);
    } finally {
      setLoading(false);
    }
  }, [functionFilter]);

  useEffect(() => {
    fetchDefinitions();
  }, [fetchDefinitions]);

  const resetForm = () => {
    setFormData({
      metric_name: '',
      function: 'brokerage',
      section: 'General',
      unit: '%',
      directionality: 'higher_is_better',
      plan_value: '',
      green_threshold: 0,
      yellow_threshold: -0.05,
      description: '',
      plan_cadence: 'monthly',
      weekly_tracking_enabled: false,
      weekly_pacing_method: 'linear',
      monthly_plan_required: true,
      quarterly_plan_required: false,
      annual_plan_required: false,
      segments_enabled: false
    });
  };

  const handleAdd = async () => {
    if (!formData.metric_name) {
      toast.error('Metric name is required');
      return;
    }
    
    // Validate: weekly tracking requires monthly plan cadence
    if (formData.weekly_tracking_enabled && formData.plan_cadence !== 'monthly') {
      toast.error('Weekly tracking requires Monthly plan cadence');
      return;
    }
    
    try {
      const payload = {
        ...formData,
        plan_value: formData.plan_value ? parseFloat(formData.plan_value) : null
      };
      await axios.post(`${API}/kpi-definitions`, payload);
      toast.success('KPI definition created');
      setIsAddDialogOpen(false);
      resetForm();
      fetchDefinitions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create definition');
    }
  };

  const handleEdit = async () => {
    if (!formData.metric_name) {
      toast.error('Metric name is required');
      return;
    }
    
    // Validate: weekly tracking requires monthly plan cadence
    if (formData.weekly_tracking_enabled && formData.plan_cadence !== 'monthly') {
      toast.error('Weekly tracking requires Monthly plan cadence');
      return;
    }
    
    try {
      await axios.put(`${API}/kpi-definitions/${selectedDef.id}`, {
        metric_name: formData.metric_name,
        section: formData.section,
        unit: formData.unit,
        directionality: formData.directionality,
        plan_value: formData.plan_value ? parseFloat(formData.plan_value) : null,
        green_threshold: formData.green_threshold,
        yellow_threshold: formData.yellow_threshold,
        description: formData.description,
        // Plan-aware fields
        plan_cadence: formData.plan_cadence,
        weekly_tracking_enabled: formData.weekly_tracking_enabled,
        weekly_pacing_method: formData.weekly_pacing_method,
        monthly_plan_required: formData.monthly_plan_required,
        quarterly_plan_required: formData.quarterly_plan_required,
        annual_plan_required: formData.annual_plan_required,
        // Vertical/Segment tracking
        segments_enabled: formData.segments_enabled
      });
      toast.success('KPI definition updated');
      setIsEditDialogOpen(false);
      setSelectedDef(null);
      fetchDefinitions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update definition');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/kpi-definitions/${selectedDef.id}`);
      toast.success('KPI definition deleted');
      setIsDeleteDialogOpen(false);
      setSelectedDef(null);
      fetchDefinitions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete definition');
    }
  };

  // Toggle Board View visibility in real-time
  const toggleBoardVisibility = async (def) => {
    const newValue = !def.is_board_visible;
    
    // Optimistic update
    setDefinitions(prev => prev.map(d => 
      d.id === def.id ? { ...d, is_board_visible: newValue } : d
    ));
    
    try {
      await axios.put(`${API}/kpi-definitions/${def.id}`, {
        is_board_visible: newValue
      });
      toast.success(newValue 
        ? `"${def.metric_name}" added to Board View` 
        : `"${def.metric_name}" removed from Board View`
      );
    } catch (error) {
      // Revert on error
      setDefinitions(prev => prev.map(d => 
        d.id === def.id ? { ...d, is_board_visible: !newValue } : d
      ));
      toast.error('Failed to update Board View visibility');
    }
  };

  // Monthly Plans Management
  const openMonthlyPlansDialog = async (def) => {
    setSelectedDef(def);
    setMonthlyPlans({});
    
    try {
      // Fetch existing monthly plans for this KPI
      const response = await axios.get(`${API}/kpi-values/monthly-plans`, {
        params: {
          metric_name: def.metric_name,
          function: def.function,
          year: selectedYear
        }
      });
      
      // Convert to object keyed by month
      const plans = {};
      response.data.forEach(p => {
        const month = p.period_start_date.substring(5, 7);
        plans[month] = p.plan_value;
      });
      setMonthlyPlans(plans);
    } catch (error) {
      console.error('Failed to fetch monthly plans:', error);
    }
    
    setIsMonthlyPlansDialogOpen(true);
  };

  const saveMonthlyPlans = async () => {
    setSavingPlans(true);
    try {
      const promises = Object.entries(monthlyPlans).map(([month, planValue]) => {
        if (planValue === '' || planValue === null || planValue === undefined) return null;
        
        const daysInMonth = new Date(selectedYear, parseInt(month), 0).getDate();
        return axios.post(`${API}/kpi-values/inline-update`, {
          metric_name: selectedDef.metric_name,
          function: selectedDef.function,
          period_type: 'monthly',
          period_start_date: `${selectedYear}-${month}-01`,
          period_end_date: `${selectedYear}-${month}-${daysInMonth}`,
          year: selectedYear,
          plan_value: parseFloat(planValue)
        });
      }).filter(Boolean);
      
      await Promise.all(promises);
      toast.success(`Monthly plans saved for ${selectedDef.metric_name}`);
      setIsMonthlyPlansDialogOpen(false);
    } catch (error) {
      toast.error('Failed to save monthly plans');
    } finally {
      setSavingPlans(false);
    }
  };

  const openEditDialog = (def) => {
    setSelectedDef(def);
    setFormData({
      metric_name: def.metric_name,
      function: def.function,
      section: def.section || 'General',
      unit: def.unit,
      directionality: def.directionality,
      plan_value: def.plan_value || '',
      green_threshold: def.green_threshold || 0,
      yellow_threshold: def.yellow_threshold || -0.05,
      description: def.description || '',
      // Plan-aware fields
      plan_cadence: def.plan_cadence || 'monthly',
      weekly_tracking_enabled: def.weekly_tracking_enabled || false,
      weekly_pacing_method: def.weekly_pacing_method || 'linear',
      monthly_plan_required: def.monthly_plan_required !== false,
      quarterly_plan_required: def.quarterly_plan_required || false,
      annual_plan_required: def.annual_plan_required || false,
      // Vertical/Segment tracking
      segments_enabled: def.segments_enabled || false
    });
    setIsEditDialogOpen(true);
  };

  const getFunctionIcon = (func) => {
    const dept = departments.find(d => d.value === func);
    return dept ? dept.icon : Settings;
  };

  const stats = departments.map(d => ({
    ...d,
    count: definitions.filter(def => def.function === d.value).length
  }));

  const canEdit = isAdmin();

  return (
    <Layout>
      <Header title="KPI Definitions" subtitle="Manage metric definitions and thresholds" />
      
      <div className="p-4 sm:p-6 space-y-6" data-testid="kpi-definitions">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {stats.map((dept) => {
            const Icon = dept.icon;
            return (
              <div key={dept.value} className="bg-white rounded-lg border border-slate-200 p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-500">{dept.label}</span>
                  <Icon className="w-5 h-5 text-slate-400" />
                </div>
                <p className="text-2xl font-bold text-slate-900 mt-2">{dept.count}</p>
                <p className="text-xs text-slate-500">metrics</p>
              </div>
            );
          })}
        </div>

        {/* Filters and Add */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Filter className="w-4 h-4 text-slate-400" />
            <Select value={functionFilter} onValueChange={setFunctionFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Functions</SelectItem>
                {departments.map(d => (
                  <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {canEdit && (
            <Button onClick={() => { resetForm(); setIsAddDialogOpen(true); }} className="bg-[#2563EB]">
              <Plus className="w-4 h-4 mr-2" />
              Add KPI Definition
            </Button>
          )}
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Metric Name</TableHead>
                <TableHead>Function</TableHead>
                <TableHead>Unit</TableHead>
                <TableHead>Plan/Target</TableHead>
                <TableHead>Direction</TableHead>
                <TableHead className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Eye className="w-4 h-4" />
                    Board View
                  </div>
                </TableHead>
                {canEdit && <TableHead className="w-[100px]">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {definitions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canEdit ? 8 : 7} className="text-center py-8 text-slate-400">
                    No KPI definitions found
                  </TableCell>
                </TableRow>
              ) : (
                definitions.map((def) => {
                  const Icon = getFunctionIcon(def.function);
                  return (
                    <TableRow key={def.id}>
                      <TableCell className="font-medium">{def.metric_name}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4 text-slate-400" />
                          <span className="capitalize">{def.function}</span>
                        </div>
                      </TableCell>
                      <TableCell>{def.unit}</TableCell>
                      <TableCell>
                        {def.plan_value != null ? (
                          <span className="font-medium text-blue-600">
                            {def.unit === '$' || def.unit === '$M' || def.unit === '$K' 
                              ? `${def.unit}${def.plan_value.toLocaleString()}`
                              : `${def.plan_value.toLocaleString()}${def.unit === '%' ? '%' : ` ${def.unit}`}`
                            }
                          </span>
                        ) : (
                          <span className="text-slate-400 text-sm">Not set</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {def.directionality === 'higher_is_better' ? (
                            <TrendingUp className="w-4 h-4 text-emerald-500" />
                          ) : (
                            <TrendingDown className="w-4 h-4 text-blue-500" />
                          )}
                          <span className="text-xs text-slate-500">
                            {def.directionality === 'higher_is_better' ? 'Higher' : 'Lower'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center">
                          <Switch
                            checked={def.is_board_visible !== false}
                            onCheckedChange={() => toggleBoardVisibility(def)}
                            disabled={!canEdit}
                            data-testid={`board-toggle-${def.id}`}
                          />
                        </div>
                      </TableCell>
                      {canEdit && (
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8" 
                              onClick={() => openMonthlyPlansDialog(def)}
                              title="Set Monthly Plans"
                            >
                              <Calendar className="w-4 h-4 text-blue-500" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditDialog(def)}>
                              <Pencil className="w-4 h-4 text-slate-500" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8"
                              onClick={() => { setSelectedDef(def); setIsDeleteDialogOpen(true); }}
                            >
                              <Trash2 className="w-4 h-4 text-rose-500" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Add Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add KPI Definition</DialogTitle>
            <DialogDescription>Create a new KPI metric definition.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Metric Name *</Label>
              <Input
                placeholder="e.g., Premium Written"
                value={formData.metric_name}
                onChange={(e) => setFormData({ ...formData, metric_name: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Function *</Label>
                <Select value={formData.function} onValueChange={(v) => setFormData({ ...formData, function: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {departments.map(d => (
                      <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Unit *</Label>
                <Select value={formData.unit} onValueChange={(v) => setFormData({ ...formData, unit: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {units.map(u => (
                      <SelectItem key={u} value={u}>{u}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Direction</Label>
              <Select value={formData.directionality} onValueChange={(v) => setFormData({ ...formData, directionality: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="higher_is_better">Higher is Better (↑)</SelectItem>
                  <SelectItem value="lower_is_better">Lower is Better (↓)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Plan/Target Value</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="e.g., 1500000"
                value={formData.plan_value}
                onChange={(e) => setFormData({ ...formData, plan_value: e.target.value })}
              />
              <p className="text-xs text-slate-500">The goal/target value for this KPI</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Green Threshold (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.green_threshold * 100}
                  onChange={(e) => setFormData({ ...formData, green_threshold: parseFloat(e.target.value) / 100 })}
                />
              </div>
              <div className="space-y-2">
                <Label>Yellow Threshold (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.yellow_threshold * 100}
                  onChange={(e) => setFormData({ ...formData, yellow_threshold: parseFloat(e.target.value) / 100 })}
                />
              </div>
            </div>
            
            {/* Plan & Pacing Settings */}
            <div className="border-t pt-4 mt-2">
              <h4 className="text-sm font-semibold text-slate-900 mb-3">Plan & Pacing Settings</h4>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="space-y-2">
                  <Label>Plan Cadence</Label>
                  <Select 
                    value={formData.plan_cadence} 
                    onValueChange={(v) => {
                      if (v !== 'monthly' && formData.weekly_tracking_enabled) {
                        setFormData({ ...formData, plan_cadence: v, weekly_tracking_enabled: false });
                      } else {
                        setFormData({ ...formData, plan_cadence: v });
                      }
                    }}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {planCadences.map(c => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">Level at which plan values are defined</p>
                </div>
                <div className="space-y-2">
                  <Label>Weekly Tracking</Label>
                  <Select 
                    value={formData.weekly_tracking_enabled ? 'true' : 'false'}
                    onValueChange={(v) => setFormData({ ...formData, weekly_tracking_enabled: v === 'true' })}
                    disabled={formData.plan_cadence !== 'monthly'}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">Enabled</SelectItem>
                      <SelectItem value="false">Disabled</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">
                    {formData.plan_cadence !== 'monthly' 
                      ? 'Requires Monthly plan cadence'
                      : 'Track weekly progress toward monthly plan'}
                  </p>
                </div>
              </div>
              
              {formData.weekly_tracking_enabled && (
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-800">
                      <strong>Weekly Pacing:</strong> This KPI will be tracked weekly toward the Monthly plan. 
                      Weekly plan = Monthly plan ÷ 4
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Monthly Plan Target ({formData.unit})</Label>
                    <Input
                      type="number"
                      step="any"
                      placeholder="e.g., 100"
                      value={formData.plan_value || ''}
                      onChange={(e) => setFormData({ ...formData, plan_value: e.target.value ? parseFloat(e.target.value) : null })}
                    />
                    <p className="text-xs text-slate-500">
                      {formData.plan_value 
                        ? `Weekly plan will be: ${(formData.plan_value / 4).toFixed(2)} ${formData.unit}`
                        : 'Set the monthly target to auto-calculate weekly plan'}
                    </p>
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <Label className="text-sm">Plan Requirements</Label>
                <div className="grid grid-cols-3 gap-4">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={formData.monthly_plan_required}
                      onChange={(e) => setFormData({ ...formData, monthly_plan_required: e.target.checked })}
                      className="rounded border-slate-300"
                    />
                    Monthly
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={formData.quarterly_plan_required}
                      onChange={(e) => setFormData({ ...formData, quarterly_plan_required: e.target.checked })}
                      className="rounded border-slate-300"
                    />
                    Quarterly
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={formData.annual_plan_required}
                      onChange={(e) => setFormData({ ...formData, annual_plan_required: e.target.checked })}
                      className="rounded border-slate-300"
                    />
                    Annual
                  </label>
                </div>
                <p className="text-xs text-slate-500">Which cadences require a plan value during upload</p>
              </div>
            </div>
            
            {/* Vertical/Segment Tracking - Only for Brokerage and MGA */}
            {(formData.function === 'brokerage' || formData.function === 'mga') && (
              <div className="border-t pt-4 mt-2">
                <h4 className="text-sm font-semibold text-slate-900 mb-3">Vertical Tracking</h4>
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <Label className="text-sm font-medium">Enable Verticals</Label>
                    <p className="text-xs text-slate-500 mt-1">
                      {formData.function === 'brokerage' 
                        ? 'Track by: Youth & Amateur Sports, Fitness, College/NIL, Small Business'
                        : 'Track by: Youth & Amateur Sports, Fitness, K-12, College/NIL'}
                    </p>
                  </div>
                  <Switch
                    checked={formData.segments_enabled}
                    onCheckedChange={(checked) => setFormData({ ...formData, segments_enabled: checked })}
                  />
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                placeholder="Brief description of this metric"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAdd} className="bg-[#2563EB]">Create Definition</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit KPI Definition</DialogTitle>
            <DialogDescription>Update the KPI definition settings</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Metric Name *</Label>
              <Input
                placeholder="e.g., Premium Written"
                value={formData.metric_name}
                onChange={(e) => setFormData({ ...formData, metric_name: e.target.value })}
                data-testid="edit-metric-name"
              />
              <p className="text-xs text-slate-500">Renaming will update the definition. Existing data references will need to be updated manually.</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Function *</Label>
                <Select value={formData.function} onValueChange={(v) => setFormData({ ...formData, function: v })}>
                  <SelectTrigger data-testid="edit-function-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {departments.map(d => (
                      <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-amber-600">Changing function will move this KPI to another dashboard</p>
              </div>
              <div className="space-y-2">
                <Label>Unit</Label>
                <Select value={formData.unit} onValueChange={(v) => setFormData({ ...formData, unit: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {units.map(u => (
                      <SelectItem key={u} value={u}>{u}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Direction</Label>
              <Select value={formData.directionality} onValueChange={(v) => setFormData({ ...formData, directionality: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="higher_is_better">Higher is Better</SelectItem>
                  <SelectItem value="lower_is_better">Lower is Better</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Plan/Target Value</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="e.g., 1500000"
                value={formData.plan_value}
                onChange={(e) => setFormData({ ...formData, plan_value: e.target.value })}
              />
              <p className="text-xs text-slate-500">The goal/target value for this KPI (used when no period-specific plan exists)</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Green Threshold (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.green_threshold * 100}
                  onChange={(e) => setFormData({ ...formData, green_threshold: parseFloat(e.target.value) / 100 })}
                />
              </div>
              <div className="space-y-2">
                <Label>Yellow Threshold (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.yellow_threshold * 100}
                  onChange={(e) => setFormData({ ...formData, yellow_threshold: parseFloat(e.target.value) / 100 })}
                />
              </div>
            </div>
            
            {/* Plan-Aware Settings */}
            <div className="border-t pt-4 mt-2">
              <h4 className="text-sm font-semibold text-slate-900 mb-3">Plan & Pacing Settings</h4>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="space-y-2">
                  <Label>Plan Cadence</Label>
                  <Select 
                    value={formData.plan_cadence} 
                    onValueChange={(v) => {
                      // If changing away from monthly, disable weekly tracking
                      if (v !== 'monthly' && formData.weekly_tracking_enabled) {
                        setFormData({ ...formData, plan_cadence: v, weekly_tracking_enabled: false });
                      } else {
                        setFormData({ ...formData, plan_cadence: v });
                      }
                    }}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {planCadences.map(c => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">Level at which plan values are defined</p>
                </div>
                <div className="space-y-2">
                  <Label>Weekly Tracking</Label>
                  <Select 
                    value={formData.weekly_tracking_enabled ? 'true' : 'false'}
                    onValueChange={(v) => setFormData({ ...formData, weekly_tracking_enabled: v === 'true' })}
                    disabled={formData.plan_cadence !== 'monthly'}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">Enabled</SelectItem>
                      <SelectItem value="false">Disabled</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">
                    {formData.plan_cadence !== 'monthly' 
                      ? 'Requires Monthly plan cadence'
                      : 'Track weekly progress toward monthly plan'}
                  </p>
                </div>
              </div>
              
              {formData.weekly_tracking_enabled && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-blue-800">
                    <strong>Weekly Pacing Enabled:</strong> This KPI is tracked weekly to pace toward the Monthly plan. 
                    Weekly plan values are calculated automatically based on elapsed days in the month.
                  </p>
                </div>
              )}
              
              <div className="space-y-2">
                <Label className="text-sm">Plan Requirements</Label>
                <div className="grid grid-cols-3 gap-4">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={formData.monthly_plan_required}
                      onChange={(e) => setFormData({ ...formData, monthly_plan_required: e.target.checked })}
                      className="rounded border-slate-300"
                    />
                    Monthly
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={formData.quarterly_plan_required}
                      onChange={(e) => setFormData({ ...formData, quarterly_plan_required: e.target.checked })}
                      className="rounded border-slate-300"
                    />
                    Quarterly
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={formData.annual_plan_required}
                      onChange={(e) => setFormData({ ...formData, annual_plan_required: e.target.checked })}
                      className="rounded border-slate-300"
                    />
                    Annual
                  </label>
                </div>
                <p className="text-xs text-slate-500">Which cadences require a plan value during upload</p>
              </div>
            </div>
            
            {/* Vertical/Segment Tracking - Only for Brokerage and MGA */}
            {(formData.function === 'brokerage' || formData.function === 'mga') && (
              <div className="border-t pt-4 mt-2">
                <h4 className="text-sm font-semibold text-slate-900 mb-3">Vertical Tracking</h4>
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <Label className="text-sm font-medium">Enable Verticals</Label>
                    <p className="text-xs text-slate-500 mt-1">
                      {formData.function === 'brokerage' 
                        ? 'Track by: Youth & Amateur Sports, Fitness, College/NIL, Small Business'
                        : 'Track by: Youth & Amateur Sports, Fitness, K-12, College/NIL'}
                    </p>
                  </div>
                  <Switch
                    checked={formData.segments_enabled}
                    onCheckedChange={(checked) => setFormData({ ...formData, segments_enabled: checked })}
                  />
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleEdit} className="bg-[#2563EB]">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-rose-600">Delete KPI Definition</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedDef?.metric_name}"? This will not delete existing data.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Monthly Plans Dialog */}
      <Dialog open={isMonthlyPlansDialogOpen} onOpenChange={setIsMonthlyPlansDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-500" />
              Set Monthly Plans - {selectedYear}
            </DialogTitle>
            <DialogDescription>
              Set plan targets for each month for "{selectedDef?.metric_name}". 
              {selectedDef?.weekly_tracking_enabled && (
                <span className="block mt-1 text-blue-600">
                  Weekly plan = Monthly plan ÷ 4
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="grid grid-cols-4 gap-3">
              {months.map(({ key, label }) => (
                <div key={key} className="space-y-1">
                  <Label className="text-xs font-medium text-slate-600">{label}</Label>
                  <Input
                    type="number"
                    step="any"
                    placeholder="—"
                    value={monthlyPlans[key] || ''}
                    onChange={(e) => setMonthlyPlans(prev => ({
                      ...prev,
                      [key]: e.target.value
                    }))}
                    className="h-9 text-sm"
                  />
                  {selectedDef?.weekly_tracking_enabled && monthlyPlans[key] && (
                    <p className="text-[10px] text-blue-500">
                      Weekly: {(parseFloat(monthlyPlans[key]) / 4).toFixed(1)}
                    </p>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-4 p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-600">
                <strong>Unit:</strong> {selectedDef?.unit} | 
                <strong className="ml-2">Function:</strong> {selectedDef?.function}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsMonthlyPlansDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveMonthlyPlans} className="bg-[#2563EB]" disabled={savingPlans}>
              {savingPlans ? 'Saving...' : 'Save Monthly Plans'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default KPIDefinitions;
